import { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '../App';
import { toast } from 'sonner';

const translations = {
  fr: {
    title: 'Assistant Biblique IA',
    placeholder: 'Posez une question sur la Bible...',
    send: 'Envoyer',
    examples: [
      'Fais-moi un résumé de ma lecture d\'aujourd\'hui',
      'Quelle est la signification de ce passage ?',
      'Comment puis-je appliquer cela dans ma vie ?'
    ]
  },
  en: {
    title: 'AI Bible Assistant',
    placeholder: 'Ask a question about the Bible...',
    send: 'Send',
    examples: [
      'Give me a summary of today\'s reading',
      'What is the meaning of this passage?',
      'How can I apply this in my life?'
    ]
  },
  es: {
    title: 'Asistente Bíblico IA',
    placeholder: 'Haz una pregunta sobre la Biblia...',
    send: 'Enviar',
    examples: [
      'Hazme un resumen de mi lectura de hoy',
      '¿Cuál es el significado de este pasaje?',
      '¿Cómo puedo aplicar esto en mi vida?'
    ]
  }
};

const ChatBot = ({ isOpen, onClose, language, currentDay }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const t = translations[language];

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await api.post('/chat', {
        message: userMessage,
        language: language,
        day: currentDay
      });

      // Add AI response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Erreur lors de la communication avec l\'assistant');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setInputMessage(example);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none">
      <Card className="w-full max-w-md h-[600px] flex flex-col glass-effect border-amber-300 pointer-events-auto">
        <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-amber-200">
          <CardTitle className="text-amber-900">{t.title}</CardTitle>
          <Button
            data-testid="close-chatbot"
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-amber-800"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
          <div className="flex-1 overflow-y-auto mb-4 space-y-4">
            {messages.length === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-amber-700 mb-4">
                  {language === 'fr' ? 'Exemples de questions :' : language === 'en' ? 'Example questions:' : 'Ejemplos de preguntas:'}
                </p>
                {t.examples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example)}
                    className="w-full text-left p-3 text-sm bg-amber-50 hover:bg-amber-100 rounded-lg text-amber-800 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-amber-100 ml-8 text-amber-900'
                      : 'bg-amber-50 mr-8 text-amber-800'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))
            )}
            {isLoading && (
              <div className="flex items-center gap-2 text-amber-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{language === 'fr' ? 'Réflexion...' : language === 'en' ? 'Thinking...' : 'Pensando...'}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Input
              data-testid="chat-input"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={t.placeholder}
              className="flex-1 border-amber-300"
              disabled={isLoading}
            />
            <Button
              data-testid="send-message"
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="btn-primary"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatBot;
